/**
 * Alta de un perfil PML (Paid Media) desde /brain/clients → Nueva solicitud → destino
 * Paid Media (T6, 19/10/2026). Cinco pasos; al confirmar pide la preview al backend
 * (`brain.previewProfile`: el JSON que se va a escribir + chequeos contra Google) y abre
 * el PR con `brain.proposeProfile`. Si `/add-client` (MCP) dejó un borrador, se ofrece
 * cargarlo. Los campos son los MISMOS que manda el MCP (shared/brain-profile-proposal.ts).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, ChevronLeft, ChevronRight, Plus, RefreshCw, Trash2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MultiSelectPopover } from '@/components/ui/multi-select-popover';
import { TagInput } from '@/components/ui/tag-input';
import { SlackChannelPicker, type PickerChannel } from '@/components/brain/SlackChannelPicker';
import { StepBar } from '@/components/brain/StepBar';
import { PrOpenedNotice } from '@/components/brain/PrOpenedNotice';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/contexts/SharedAuthContext';
import { BRAIN_CLIENTS } from '../../../../shared/types/brain';
import { BRAIN_PROFILE_BU_RE, BRAIN_PROFILE_WEEKDAYS, isWinclapEmail } from '../../../../shared/brain-profile-config';
import { BRAIN_CLIENT_ID_RE } from '../../../../shared/brain-client-config';
import {
  buSlugOf,
  chatSpaceFromInput,
  emptyProfileProposalFields,
  recurringEventIdFromInput,
  type ProfileProposalFields,
  type ProfileSourceCheck,
} from '../../../../shared/brain-profile-proposal';

const NEW_CLIENT = '__new__';
const STEPS = 5;

interface MeetingRow { raw: string; label: string }

interface State {
  fields: ProfileProposalFields;
  channelNames: Record<string, string>;
  clientChoice: string;          // id del registro o NEW_CLIENT
  newClientName: string;
  clientIdTouched: boolean;
  buTouched: boolean;
  meetings: MeetingRow[];
  calendarOf: string;
}

function initialState(owner: string): State {
  return {
    fields: emptyProfileProposalFields({ owner }),
    channelNames: {}, clientChoice: '', newClientName: '', clientIdTouched: false, buTouched: false,
    meetings: [], calendarOf: '',
  };
}

/** El borrador del MCP trae los campos planos (y los nombres de canal que resolvió); lo demás del estado se deriva. */
function stateFromFields(f: ProfileProposalFields, owner: string, channelNames: Record<string, string> = {}, newClientName = ''): State {
  const known = !!BRAIN_CLIENTS[f.clientId];
  return {
    fields: { ...emptyProfileProposalFields({ owner }), ...f, owner: f.owner || owner },
    channelNames,
    clientChoice: f.clientId ? (known ? f.clientId : NEW_CLIENT) : '',
    newClientName: known ? '' : newClientName || f.clientId,
    clientIdTouched: !known && !!f.clientId, buTouched: !!f.bu,
    meetings: f.meetings.map((m) => ({ raw: m.recurringEventId, label: m.label ?? '' })),
    calendarOf: f.meetings.find((m) => m.calendarOf)?.calendarOf ?? '',
  };
}

export function BrainProfileForm({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const me = user?.email ?? '';
  const [step, setStep] = useState(0);
  const [st, setSt] = useState<State>(() => initialState(me));
  const [touched, setTouched] = useState(false);
  const setFields = (patch: Partial<ProfileProposalFields>) => { setTouched(true); setSt((s) => ({ ...s, fields: { ...s.fields, ...patch } })); };
  const setState = (patch: Partial<State>) => { setTouched(true); setSt((s) => ({ ...s, ...patch })); };

  useEffect(() => { if (me && !st.fields.owner) setSt((s) => ({ ...s, fields: { ...s.fields, owner: me } })); }, [me, st.fields.owner]);

  // Borrador que dejó /add-client (MCP)
  const draftQ = trpc.brain.getProfileDraft.useQuery(undefined, { staleTime: 30_000, retry: false });
  const discardDraft = trpc.brain.discardProfileDraft.useMutation({ onSuccess: () => draftQ.refetch() });
  const [draftHandled, setDraftHandled] = useState(false);
  const draft = !draftHandled && !touched ? draftQ.data?.draft ?? null : null;

  const clientId = st.clientChoice === NEW_CLIENT ? st.fields.clientId : st.clientChoice;
  const clientChannelsQ = trpc.brain.clientChannels.useQuery({ clientId }, { enabled: !!clientId && st.clientChoice !== NEW_CLIENT, staleTime: 60_000 });
  const clientExtra: PickerChannel[] = useMemo(
    () => (clientChannelsQ.data?.channels ?? []).map((c) => ({ id: c.channelId, name: c.channelName, hint: c.external ? t('brain.clients.profile.ch_external') : t('brain.clients.profile.ch_of_client') })),
    [clientChannelsQ.data, t]
  );

  const meetingsParsed = st.meetings.map((m) => ({ ...m, id: recurringEventIdFromInput(m.raw) }));
  const syncMeetings = (rows: MeetingRow[], calendarOf = st.calendarOf) => {
    const cal = calendarOf.trim().toLowerCase();
    setState({
      meetings: rows, calendarOf,
      fields: {
        ...st.fields,
        meetings: rows.map((m) => ({ recurringEventId: recurringEventIdFromInput(m.raw) ?? m.raw.trim(), ...(m.label.trim() ? { label: m.label.trim() } : {}), ...(cal ? { calendarOf: cal } : {}) })).filter((m) => m.recurringEventId),
      },
    });
  };

  const f = st.fields;
  const canAdvance = (): boolean => {
    if (step === 0) {
      const okClient = st.clientChoice === NEW_CLIENT ? BRAIN_CLIENT_ID_RE.test(f.clientId) && st.newClientName.trim().length >= 2 : !!st.clientChoice;
      return okClient && BRAIN_PROFILE_BU_RE.test(f.bu) && f.buName.trim().length > 0 && isWinclapEmail(f.owner) && f.managerName.trim().length > 0 && (!f.managerEmail || isWinclapEmail(f.managerEmail));
    }
    if (step === 1) return (f.channelIds.length > 0 || f.sharedChannelIds.length > 0) && (f.sharedChannelIds.length === 0 || f.buFilter.length > 0);
    if (step === 2) return meetingsParsed.every((m) => !m.raw.trim() || m.id) && (!st.calendarOf || isWinclapEmail(st.calendarOf));
    if (step === 3) {
      const gmailOk = !f.gmailLabel?.trim() || (f.gmailMailboxes.length > 0 && f.gmailMailboxes.every(isWinclapEmail));
      const chatOk = f.chatSpaces.every((s) => !!chatSpaceFromInput(s)) && (!f.chatReader || isWinclapEmail(f.chatReader));
      const statusOk = !f.statusEnabled || (f.statusDays.length > 0 && !!f.statusOutputChannel);
      return gmailOk && chatOk && statusOk;
    }
    return true;
  };

  const proposalInput = () => ({
    fields: { ...f, meetings: f.meetings.filter((m) => m.recurringEventId) },
    channelNames: st.channelNames,
    ...(st.clientChoice === NEW_CLIENT ? { newClient: { displayName: st.newClientName.trim() } } : {}),
  });

  // Paso 5: preview + envío
  const preview = trpc.brain.previewProfile.useMutation();
  const propose = trpc.brain.proposeProfile.useMutation();
  const [prUrl, setPrUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // La preview corre al entrar al paso 5 y solo si cambió algo desde la última (StrictMode monta dos veces en dev).
  const lastPreview = useRef<string>('');
  const runPreview = (force = false) => {
    const input = proposalInput();
    const sig = JSON.stringify(input);
    if (!force && sig === lastPreview.current) return;
    lastPreview.current = sig;
    setError(null);
    preview.mutateAsync(input).catch((e: Error) => { lastPreview.current = ''; setError(e.message); });
  };
  useEffect(() => {
    if (step === 4) runPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const submit = async () => {
    setError(null);
    try {
      const r = await propose.mutateAsync(proposalInput());
      setPrUrl(r.prUrl);
      if (r.warning) setError(r.warning);
    } catch (e) {
      const msg = (e as Error).message ?? '';
      // Un timeout o corte de red después de abrir el PR (CloudFront corta a los 30 s): puede que ya exista.
      setError(/failed to fetch|network|load failed|timeout/i.test(msg) ? t('brain.clients.profile.propose_network_hint') : msg);
    }
  };

  const labels = Array.from({ length: STEPS }, (_, i) => t(`brain.clients.profile.steps.${i}`));
  const weekdayOptions = BRAIN_PROFILE_WEEKDAYS.map((d) => ({ value: d, label: t(`brain.clients.profile.weekday.${d}`) }));

  if (prUrl) {
    return (
      <PrOpenedNotice prUrl={prUrl} title={t('brain.clients.form.success_new_title')} body={t('brain.clients.profile.success_body', { bu: f.buName, cmd: `/pml ${clientId} ${f.bu}` })}>
        {error && <p className="text-xs text-amber-700">{error}</p>}
        <div><Button variant="outline" onClick={onDone}>{t('brain.clients.volver_a_mis_solicitudes')}</Button></div>
      </PrOpenedNotice>
    );
  }

  return (
    <div className="space-y-0">
      {draft && (
        <div className="flex items-start gap-2 p-3 mb-5 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-800">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-blue-600" />
          <div className="flex-1 space-y-1">
            <p><strong>{t('brain.clients.profile.draft_title')}</strong> {t('brain.clients.profile.draft_body', { source: draft.source, when: new Date(draft.savedAt).toLocaleString() })}</p>
            {draft.pending.length > 0 && <ul className="list-disc ml-4">{draft.pending.map((p, i) => <li key={i}>{p}</li>)}</ul>}
            <div className="flex gap-3 pt-1">
              <button type="button" className="font-semibold hover:underline" onClick={() => { setSt(stateFromFields(draft.fields, me, draft.channelNames ?? {}, draft.newClientName ?? '')); setDraftHandled(true); setTouched(true); }}>{t('brain.clients.profile.draft_load')}</button>
              <button type="button" className="hover:underline disabled:opacity-50" disabled={discardDraft.isPending} onClick={() => { setDraftHandled(true); discardDraft.mutate(); }}>{t('brain.clients.profile.draft_discard')}</button>
            </div>
          </div>
        </div>
      )}

      <StepBar current={step} labels={labels} />

      {step === 0 && (
        <div className="space-y-5">
          <div>
            <Label className="text-xs font-semibold text-gray-700">{t('brain.clients.profile.client_label')}</Label>
            <select
              value={st.clientChoice}
              onChange={(e) => {
                const v = e.target.value;
                setState({ clientChoice: v, fields: { ...f, clientId: v === NEW_CLIENT ? (st.clientIdTouched ? f.clientId : buSlugOf(st.newClientName)) : v, channelIds: [], sharedChannelIds: [] } });
              }}
              className="mt-1.5 w-full px-3 py-2 text-sm border rounded-lg bg-white focus:outline-hidden focus:ring-1 focus:ring-[#FF6B6B]"
            >
              <option value="">{t('brain.clients.elegi_un_cliente')}</option>
              {Object.entries(BRAIN_CLIENTS).map(([id, name]) => <option key={id} value={id}>{name}</option>)}
              <option value={NEW_CLIENT}>{t('brain.clients.profile.client_new_option')}</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">{t('brain.clients.profile.client_hint')}</p>
          </div>
          {st.clientChoice === NEW_CLIENT && (
            <div className="grid sm:grid-cols-2 gap-3 p-3 rounded-lg border bg-gray-50">
              <div>
                <Label htmlFor="pml-newClientName">{t('brain.clients.step1.display_name_label')}</Label>
                <Input id="pml-newClientName" className="mt-1" value={st.newClientName} placeholder={t('brain.clients.step1.display_name_placeholder')}
                  onChange={(e) => setState({ newClientName: e.target.value, fields: { ...f, clientId: st.clientIdTouched ? f.clientId : buSlugOf(e.target.value) } })} />
              </div>
              <div>
                <Label htmlFor="pml-clientId">{t('brain.clients.step1.client_id_label')}</Label>
                <Input id="pml-clientId" className="mt-1 font-mono text-sm" value={f.clientId}
                  onChange={(e) => setState({ clientIdTouched: true, fields: { ...f, clientId: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') } })} />
              </div>
              <p className="sm:col-span-2 text-xs text-gray-500">{t('brain.clients.profile.client_new_hint')}</p>
            </div>
          )}
          <div className="grid sm:grid-cols-[1fr_auto_auto] gap-3">
            <div>
              <Label htmlFor="pml-buName">{t('brain.clients.profile.bu_name')}</Label>
              <Input id="pml-buName" className="mt-1" placeholder={t('brain.clients.profile.bu_name_placeholder')} value={f.buName}
                onChange={(e) => setFields({ buName: e.target.value, bu: st.buTouched ? f.bu : buSlugOf(e.target.value) })} />
            </div>
            <div>
              <Label htmlFor="pml-bu">{t('brain.clients.profile.bu_slug')}</Label>
              <Input id="pml-bu" className="mt-1 font-mono text-sm w-32" value={f.bu}
                onChange={(e) => { setState({ buTouched: true }); setFields({ bu: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }); }} />
            </div>
            <div>
              <Label htmlFor="pml-emoji">{t('brain.clients.profile.bu_emoji')}</Label>
              <Input id="pml-emoji" className="mt-1 w-16 text-center" value={f.buEmoji ?? ''} maxLength={4} onChange={(e) => setFields({ buEmoji: e.target.value })} />
            </div>
          </div>
          <p className="text-xs text-gray-500 -mt-3">{t('brain.clients.profile.bu_hint', { id: `${clientId || t('brain.clients.profile.ph_client')}.${f.bu || t('brain.clients.profile.ph_bu')}` })}</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="pml-owner">{t('brain.clients.profile.owner')}</Label>
              <Input id="pml-owner" className="mt-1" value={f.owner} onChange={(e) => setFields({ owner: e.target.value })} />
              <p className="text-xs text-gray-500 mt-1">{t('brain.clients.profile.owner_hint')}</p>
            </div>
            <div>
              <Label htmlFor="pml-managerName">{t('brain.clients.profile.manager_name')}</Label>
              <Input id="pml-managerName" className="mt-1" value={f.managerName} onChange={(e) => setFields({ managerName: e.target.value })} />
              <Input className="mt-2" placeholder={t('brain.clients.profile.manager_email_placeholder')} value={f.managerEmail ?? ''} onChange={(e) => setFields({ managerEmail: e.target.value || undefined })} />
            </div>
          </div>
          <div>
            <Label>{t('brain.clients.profile.alerts_channel')}</Label>
            <p className="text-xs text-gray-500 mb-1.5">{t('brain.clients.profile.alerts_channel_hint')}</p>
            <SlackChannelPicker single selected={f.alertsChannel ? [f.alertsChannel] : []} extra={clientExtra} knownNames={st.channelNames} listClassName="max-h-32"
              onChange={(ids, names) => { setState({ channelNames: { ...names, ...st.channelNames } }); setFields({ alertsChannel: ids[0] }); }} />
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="space-y-5">
          <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-700">{t('brain.clients.profile.slack_disclaimer')}</p>
          </div>
          <div>
            <Label>{t('brain.clients.profile.own_channels')}</Label>
            <p className="text-xs text-gray-500 mb-1.5">{t('brain.clients.profile.own_channels_hint')}</p>
            <SlackChannelPicker selected={f.channelIds} extra={clientExtra} exclude={f.sharedChannelIds} knownNames={st.channelNames}
              onChange={(ids, names) => { setState({ channelNames: { ...names, ...st.channelNames } }); setFields({ channelIds: ids }); }} />
          </div>
          <div>
            <Label>{t('brain.clients.profile.shared_channels')}</Label>
            <p className="text-xs text-gray-500 mb-1.5">{t('brain.clients.profile.shared_channels_hint')}</p>
            <SlackChannelPicker selected={f.sharedChannelIds} extra={clientExtra} exclude={f.channelIds} knownNames={st.channelNames} listClassName="max-h-32"
              onChange={(ids, names) => { setState({ channelNames: { ...names, ...st.channelNames } }); setFields({ sharedChannelIds: ids }); }} />
            {f.sharedChannelIds.length > 0 && (
              <div className="mt-3">
                <Label>{t('brain.clients.profile.bu_filter')}</Label>
                <p className="text-xs text-gray-500 mb-1.5">{t('brain.clients.profile.bu_filter_hint')}</p>
                <TagInput value={f.buFilter} onChange={(v) => setFields({ buFilter: v })} listId="pml-bu-filter" placeholder={t('brain.clients.profile.bu_filter_placeholder')} />
              </div>
            )}
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>{t('brain.clients.profile.sides_client')}</Label>
              <TagInput value={f.sidesClient} onChange={(v) => setFields({ sidesClient: v })} listId="pml-sides-client" placeholder={t('brain.clients.profile.sides_placeholder')} className="mt-1" />
            </div>
            <div>
              <Label>{t('brain.clients.profile.sides_winclap')}</Label>
              <TagInput value={f.sidesWinclap} onChange={(v) => setFields({ sidesWinclap: v })} listId="pml-sides-winclap" placeholder={t('brain.clients.profile.sides_placeholder')} className="mt-1" />
            </div>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">{t('brain.clients.profile.meetings_intro')}</p>
          <div className="space-y-2">
            {meetingsParsed.map((m, i) => (
              <div key={i} className="rounded-lg border p-3 space-y-2">
                <div className="flex gap-2">
                  <Input className="flex-1 font-mono text-xs" placeholder={t('brain.clients.profile.meeting_link_placeholder')} value={m.raw}
                    onChange={(e) => syncMeetings(st.meetings.map((r, j) => (j === i ? { ...r, raw: e.target.value } : r)))} />
                  <button type="button" onClick={() => syncMeetings(st.meetings.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500" aria-label={t('brain.clients.profile.meeting_remove')}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <Input className="text-sm" placeholder={t('brain.clients.profile.meeting_label_placeholder')} value={m.label}
                  onChange={(e) => syncMeetings(st.meetings.map((r, j) => (j === i ? { ...r, label: e.target.value } : r)))} />
                {m.raw.trim() && (
                  m.id
                    ? <p className="text-xs text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {t('brain.clients.profile.meeting_parsed', { id: m.id })}</p>
                    : <p className="text-xs text-red-600 flex items-center gap-1"><XCircle className="w-3 h-3" /> {t('brain.clients.profile.meeting_unparsed')}</p>
                )}
              </div>
            ))}
          </div>
          <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => syncMeetings([...st.meetings, { raw: '', label: '' }])}>
            <Plus className="w-3.5 h-3.5" /> {t('brain.clients.profile.meeting_add')}
          </Button>
          <div>
            <Label htmlFor="pml-calendarOf">{t('brain.clients.profile.calendar_of')}</Label>
            <Input id="pml-calendarOf" className="mt-1" placeholder={f.owner} value={st.calendarOf} onChange={(e) => syncMeetings(st.meetings, e.target.value)} />
            <p className="text-xs text-gray-500 mt-1">{t('brain.clients.profile.calendar_of_hint')}</p>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-5">
          <div className="rounded-lg border p-3 space-y-3">
            <p className="text-sm font-semibold text-gray-800">{t('brain.clients.profile.gmail_title')}</p>
            <p className="text-xs text-gray-500">{t('brain.clients.profile.gmail_intro')}</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="pml-gmailLabel">{t('brain.clients.profile.gmail_label')}</Label>
                <Input id="pml-gmailLabel" className="mt-1 font-mono text-sm" placeholder={t('brain.clients.profile.gmail_label_placeholder', { client: clientId || t('brain.clients.profile.ph_client'), bu: f.bu || t('brain.clients.profile.ph_bu') })} value={f.gmailLabel ?? ''} onChange={(e) => setFields({ gmailLabel: e.target.value || undefined })} />
              </div>
              <div>
                <Label>{t('brain.clients.profile.gmail_mailboxes')}</Label>
                <TagInput value={f.gmailMailboxes} onChange={(v) => setFields({ gmailMailboxes: v.map((x) => x.toLowerCase()) })} listId="pml-mailboxes" placeholder="nombre@winclap.com" className="mt-1" />
              </div>
            </div>
          </div>
          <div className="rounded-lg border p-3 space-y-3">
            <p className="text-sm font-semibold text-gray-800">{t('brain.clients.profile.chat_title')}</p>
            <p className="text-xs text-gray-500">{t('brain.clients.profile.chat_intro')}</p>
            <TagInput value={f.chatSpaces} onChange={(v) => setFields({ chatSpaces: v.map((x) => chatSpaceFromInput(x) ?? x) })} listId="pml-spaces" placeholder="https://chat.google.com/room/…" />
            {f.chatSpaces.some((s) => !chatSpaceFromInput(s)) && <p className="text-xs text-red-600">{t('brain.clients.profile.chat_unparsed')}</p>}
            <div>
              <Label htmlFor="pml-chatReader">{t('brain.clients.profile.chat_reader')}</Label>
              <Input id="pml-chatReader" className="mt-1" placeholder={f.owner} value={f.chatReader ?? ''} onChange={(e) => setFields({ chatReader: e.target.value || undefined })} />
            </div>
          </div>
          <div className="rounded-lg border p-3 space-y-3">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input type="checkbox" className="mt-0.5 rounded border-gray-300 text-[#FF6B6B] focus:ring-[#FF6B6B]" checked={f.statusEnabled} onChange={(e) => setFields({ statusEnabled: e.target.checked })} />
              <div>
                <p className="text-sm font-semibold text-gray-800">{t('brain.clients.profile.status_title')}</p>
                <p className="text-xs text-gray-500">{t('brain.clients.profile.status_hint')}</p>
              </div>
            </label>
            {f.statusEnabled && (
              <div className="space-y-3 ml-6">
                <div className="flex items-center gap-3">
                  <MultiSelectPopover label={t('brain.clients.profile.status_days')} options={weekdayOptions} selected={f.statusDays} onChange={(v) => setFields({ statusDays: v })} allLabel={t('brain.clients.profile.status_days_none')} />
                </div>
                <div>
                  <Label>{t('brain.clients.profile.status_channel')}</Label>
                  <SlackChannelPicker single selected={f.statusOutputChannel ? [f.statusOutputChannel] : []} extra={clientExtra} knownNames={st.channelNames} listClassName="max-h-32"
                    onChange={(ids, names) => { setState({ channelNames: { ...names, ...st.channelNames } }); setFields({ statusOutputChannel: ids[0] }); }} />
                </div>
              </div>
            )}
            <div>
              <Label htmlFor="pml-analyticsKey">{t('brain.clients.profile.analytics_key')}</Label>
              <Input id="pml-analyticsKey" className="mt-1 font-mono text-sm w-60" value={f.analyticsKey ?? ''} onChange={(e) => setFields({ analyticsKey: e.target.value || undefined })} />
              <p className="text-xs text-gray-500 mt-1">{t('brain.clients.profile.analytics_key_hint')}</p>
            </div>
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="space-y-4">
          {preview.isPending && <p className="text-sm text-gray-500">{t('brain.clients.profile.preview_loading')}</p>}
          {preview.data && (
            <>
              <p className="text-sm text-gray-600">{t('brain.clients.profile.preview_intro', { name: preview.data.profileName })}</p>
              {preview.data.proposalInProgress && (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">{t('brain.clients.profile.preview_in_progress')}</div>
              )}
              {preview.data.clientIsNew && <p className="text-xs text-blue-700">{t('brain.clients.profile.preview_new_client', { name: preview.data.clientName })}</p>}
              {preview.data.clientWithoutBq && <p className="text-xs text-amber-700">{t('brain.clients.profile.preview_client_no_bq')}</p>}
              {preview.data.channelsAddedToClient.length > 0 && (
                <p className="text-xs text-gray-600">{t('brain.clients.profile.preview_channels_added', { channels: preview.data.channelsAddedToClient.map((c) => `#${c.channelName}`).join(', ') })}</p>
              )}
              {preview.data.checks.length > 0 && (
                <ul className="text-xs space-y-1">
                  {preview.data.checks.map((c: ProfileSourceCheck, i: number) => (
                    <li key={i} className={`flex items-start gap-1.5 ${c.ok ? 'text-emerald-700' : 'text-amber-700'}`}>
                      {c.ok ? <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" /> : <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
                      <span><span className="font-mono">{c.id}</span>: {c.detail}</span>
                    </li>
                  ))}
                </ul>
              )}
              <pre className="text-[11px] leading-snug bg-gray-50 border rounded-lg p-3 overflow-x-auto max-h-72">{preview.data.profileJson}</pre>
            </>
          )}
          {error && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start justify-between gap-3">
              <span>{error}</span>
              {preview.isError && (
                <button type="button" onClick={() => runPreview(true)} disabled={preview.isPending} className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold hover:underline disabled:opacity-50">
                  <RefreshCw className="w-3 h-3" /> {t('brain.clients.profile.preview_retry')}
                </button>
              )}
            </div>
          )}
          <Button onClick={submit} disabled={propose.isPending || preview.isPending || !preview.data} className="w-full bg-[#FF6B6B] hover:bg-[#e85d5d] text-white">
            {propose.isPending ? t('brain.clients.form.opening_pr') : t('brain.clients.form.open_pr')}
          </Button>
        </div>
      )}

      <div className="flex justify-between mt-8">
        <Button variant="ghost" onClick={() => { if (step === 0) { onDone(); return; } setStep((s) => s - 1); }} className="gap-1">
          <ChevronLeft className="w-4 h-4" /> {step === 0 ? t('brain.clients.form.cancel') : t('brain.clients.form.previous')}
        </Button>
        {step < STEPS - 1 && (
          <Button onClick={() => setStep((s) => s + 1)} disabled={!canAdvance()} className="bg-[#FF6B6B] hover:bg-[#e85d5d] text-white gap-1">
            {t('brain.clients.form.next')} <ChevronRight className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
